import { configure } from '..';
import waitingRoomDispatcher from '../module/waiting-room/action/waitingRoom';

class RomeoTranscriptionService {
  constructor() {
    this.websocketPatient = null;
    this.websocketDoctor = null;
    this.audioInput = {};
    this.processor = {};
    this.isRecording = false;
    this.sessionId = null;
    this.messagesPatient = [];
    this.messagesDoctor = [];
    this.onStatusChange = null;
    this.onTranscriptUpdate = null;
    this.onSummaryUpdate = null;
    this.retryCountPatient = 0;
    this.retryCountDoctor = 0;
    this.maxRetries = 3;
    this.isNewSession = true;
    this.doctorAudioBuffers = [];
    this.patientAudioBuffers = [];
    this.doctorAudioInput = null;
    this.doctorProcessor = null;
    this.patientAudioInput = null;
    this.patientProcessor = null;
    // Create audio context
    this.doctorAudioContext = null;
    this.patientAudioContext = null;
  }

  async startAgoraTranscriptionSession({ sessionId, channelName, token }) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AI_ROMEO_URL}agora/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            session_id: sessionId,
            channel_name: channelName,
            tag: 'doctor',
          }),
        }
      );

      const data = await response.json().catch(() => null);
      return { response, data };
    } catch (error) {
      console.error('Failed to start agora transcription session:', error);
      return { response: null, data: null };
    }
  }

  async stopAgoraTranscriptionSession({ sessionId, token }) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AI_ROMEO_URL}agora/stop`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            session_id: sessionId,
          }),
        }
      );

      const data = await response.json().catch(() => null);
      return { response, data };
    } catch (error) {
      console.error('Failed to stop agora transcription session:', error);
      return { response: null, data: null };
    }
  }

  formatTranscriptTimeForWs(value) {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return new Date(value).toISOString();
    }
    return String(value);
  }

  /**
   * Forward Agora STT chunk to Romeo doctor WebSocket (backend expects ISO times + tag/session_id).
   */
  sendDoctorTranscriptWs({
    transcript,
    start_time,
    end_time,
    speaker,
    is_final,
  }) {
    const ws = this.websocketDoctor;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const body = {
      transcript,
      start_time: this.formatTranscriptTimeForWs(start_time),
      end_time: this.formatTranscriptTimeForWs(end_time),
      speaker,
      is_final: Boolean(is_final),
      tag: 'doctor',
      session_id: String(this.sessionId ?? ''),
    };

    try {
      ws.send(JSON.stringify(body));
    } catch (err) {
      console.warn('sendDoctorTranscriptWs failed:', err);
    }
  }

  async generateSummary(token, sessionId) {
    if (!token) {
      throw new Error('Token is required for summary generation');
    }
    try {
      waitingRoomDispatcher.setState('isLoadingSummary', true);
      const response = await fetch(
        `${process.env.REACT_APP_AI_ROMEO_URL}summary`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        }
      );
      waitingRoomDispatcher.setState('isLoadingSummary', false);

      if (!response.ok) {
        const errorData = await response?.json();
        console.log('errorData', errorData);
      }
      if (response.status === 200) {
        const summaryData = await response?.json();
        waitingRoomDispatcher.setState('summaryData', summaryData.summary_text);
        if (this.onSummaryUpdate) {
          this.onSummaryUpdate(summaryData.summary_text);
        }
        return summaryData.summary_text;
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      throw error;
    }
  }
  async getTranscriptionResult(token, sessionId) {
    if (!token) {
      throw new Error('Token is required for summary generation');
    }
    try {
      waitingRoomDispatcher.setState('isLoadingTranscriptionResult', true);
      const response = await fetch(
        `${process.env.REACT_APP_AI_ROMEO_URL}result?session_id=${sessionId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      waitingRoomDispatcher.setState('isLoadingTranscriptionResult', false);
      if (!response.ok) {
        const errorData = await response?.json();
        console.log('errorData', errorData);
      }
      if (response.status === 200) {
        const historyData = await response?.json();
        waitingRoomDispatcher.setState('oldData', historyData);
      }
      // return summaryData.summary_text;
    } catch (error) {
      console.error('Error getting transcription result:', error);

      throw error;
    }
  }

  async connectDoctorWebSocket(token) {
    this.sessionId = configure.store.getState().waitingRoom.callId;
    const channelId = configure.store.getState().waitingRoom.channelId;
    const channelName = encodeURIComponent(channelId || '');
    if (this.retryCountDoctor >= this.maxRetries) {
      console.error('Max retry attempts reached for doctor WebSocket');
      if (this.onStatusChange) {
        this.onStatusChange('Connection Failed: Max retries reached');
      }
      return;
    }
    // wss://61ee-27-74-255-193.ngrok-free.app/romeo/api/v1/transcribe/ws
    this.websocketDoctor = new WebSocket(
      `${process.env.REACT_APP_AI_ROMEO_URL}ws?tag=doctor&session_id=${this.sessionId}&channel_name=${channelName}`
    );

    this.websocketDoctor.onopen = () => {
      this.websocketDoctor.send(JSON.stringify({ token: token }));
      console.log('==> WebSocket Doctor connection established');
      this.startAgoraTranscriptionSession({
        sessionId: this.sessionId,
        channelName: channelId || '',
        token,
      });
      this.retryCountDoctor = 0; // Reset retry count on successful connection
      this.isRecording = true;
      // this.startRecordingDoctor();
      if (this.onStatusChange) {
        this.onStatusChange('Recording');
      }
    };

    this.websocketDoctor.onerror = (error) => {
      console.error('WebSocket error:', error);
      if (this.onStatusChange) {
        this.onStatusChange('WebSocket Error');
      }
    };

    this.websocketDoctor.onclose = () => {
      console.log('Doctor WebSocket closed');
    };
  }

  async toggleRecording(token) {
    if (!token) {
      throw new Error('Token is required to start recording');
    }

    try {
      this.sessionId = configure.store.getState().waitingRoom.callId;
      this.retryCountPatient = 0;
      this.retryCountDoctor = 0;
      console.log('==> Start to get transcription', this.sessionId);

      // Mark that this is no longer a new session after first connection
      if (this.isNewSession) {
        console.log('Starting new session - messages will be cleared');
      } else {
        console.log('Reconnecting - preserving existing messages');
      }

      // Connect doctor WebSocket only
      await this.connectDoctorWebSocket(token);

      // After successful connection, mark as no longer new session
      this.isNewSession = false;
    } catch (error) {
      console.error('Error starting recording:', error);
      if (this.onStatusChange) {
        this.onStatusChange('Error: ' + error.message);
      }
      throw error;
    }
  }

  closeWebSocketDoctor() {
    console.log(
      'Websocket Close Doctor - Starting cleanup',
      this.websocketDoctor
    );

    // Safely disconnect audio input for doctor
    if (this.audioInput && this.audioInput.doctor) {
      try {
        this.audioInput.doctor.disconnect();
        console.log('Doctor audio input disconnected');
      } catch (error) {
        console.warn('Error disconnecting doctor audio input:', error);
      }
    }

    if (this.doctorAudioContext) {
      this.doctorAudioContext.suspend();
    }
    if (this.processor && this.processor.doctor) {
      try {
        this.processor.doctor.disconnect();
        console.log('Doctor processor disconnected');
      } catch (error) {
        console.warn('Error disconnecting doctor processor:', error);
      }
    }
    // Safely close doctor WebSocket
    if (this.websocketDoctor) {
      try {
        if (this.websocketDoctor.readyState === WebSocket.OPEN) {
          console.log('==> Send Tracking Message Before Close Doctor');
          this.websocketDoctor.send(new ArrayBuffer(0));
        }
        this.websocketDoctor.close();
      } catch (error) {
        console.warn('Error closing doctor WebSocket:', error);
      }
    } else {
      console.log('Doctor WebSocket was not initialized');
    }

    // Safely close audio context if no other connections are using it
    if (
      this.doctorAudioContext &&
      this.doctorAudioContext?.state !== 'closed'
    ) {
      this.doctorAudioContext.close();
    }
  }

  stopRecording() {
    console.log('Websocket Stop Recording');
    this.isRecording = false;
    this.isNewSession = true;
    this.messagesDoctor = [];
    this.messagesPatient = [];

    // Disconnect all audio processors
    if (this.audioInput) {
      if (this.audioInput.doctor) {
        this.audioInput.doctor.disconnect();
      }
      if (this.audioInput.patient) {
        this.audioInput.patient.disconnect();
      }
    }
    if (this.processor) {
      if (this.processor.doctor) {
        this.processor.doctor.disconnect();
      }
      if (this.processor.patient) {
        this.processor.patient.disconnect();
      }
    }
    if (this.websocketPatient) {
      if (this.websocketPatient.readyState === WebSocket.OPEN) {
        console.log('==> Send Tracking Message Before Close Patient');
        this.websocketPatient.send(new ArrayBuffer(0));
      }
      this.websocketPatient.close();
      this.websocketPatient = null; // Clear reference
    }
    if (this.websocketDoctor) {
      if (this.websocketDoctor.readyState === WebSocket.OPEN) {
        console.log('==> Send Tracking Message Before Close Doctor');
        this.websocketDoctor.send(new ArrayBuffer(0));
      }
      this.websocketDoctor.close();
      this.websocketDoctor = null; // Clear reference
    }
    // Close audio contexts if they are open

    try {
      if (
        this.patientAudioContext &&
        this.patientAudioContext.state !== 'closed'
      ) {
        this.patientAudioContext.close();
      }

      if (
        this.doctorAudioContext &&
        this.doctorAudioContext.state !== 'closed'
      ) {
        this.doctorAudioContext.close();
      }
    } catch (err) {
      console.log('Error Disconnect AudioContext', err);
    }

    this.isRecording = false;
    this.isNewSession = true; // Reset to true so next session will be treated as new
    if (this.onStatusChange) {
      this.onStatusChange('Not recording');
    }
  }
}

export const romeoServiceSingleton = new RomeoTranscriptionService();
